import { useState, useRef, useEffect, useCallback } from 'react'

type FormErrors<T> = Partial<Record<keyof T, string>>

interface ParseSuccess<T> {
  success: true
  data: T
}

interface ParseFailure {
  success: false
  error: { issues: Array<{ path: PropertyKey[]; message: string }> }
}

interface FormSchema<T> {
  safeParse(data: unknown): ParseSuccess<T> | ParseFailure
}

function isKeyOf<T extends Record<string, unknown>>(
  key: unknown,
  obj: T,
): key is keyof T & string {
  return typeof key === 'string' && key in obj
}

interface UseFormOptions<T extends Record<string, unknown>> {
  schema: FormSchema<T>
  defaults: T
  initialValues?: Partial<T> | null
}

interface UseFormReturn<T extends Record<string, unknown>> {
  values: T
  errors: FormErrors<T>
  setField: <K extends keyof T>(field: K, value: T[K]) => void
  setValues: (partial: Partial<T>) => void
  validate: () => T | null
  reset: () => void
}

export function useForm<T extends Record<string, unknown>>({
  schema,
  defaults,
  initialValues,
}: UseFormOptions<T>): UseFormReturn<T> {
  const [values, setValuesState] = useState<T>(
    initialValues ? { ...defaults, ...initialValues } : defaults,
  )
  const [errors, setErrors] = useState<FormErrors<T>>({})

  const hydratedRef = useRef(Boolean(initialValues))
  useEffect(() => {
    if (initialValues && !hydratedRef.current) {
      hydratedRef.current = true
      setValuesState((prev) => ({ ...prev, ...initialValues }))
    }
  }, [initialValues])

  const setField = useCallback(
    <K extends keyof T>(field: K, value: T[K]): void => {
      setValuesState((prev) => ({ ...prev, [field]: value }))
      setErrors((prev) => {
        if (!prev[field]) return prev
        const next = { ...prev }
        delete next[field]
        return next
      })
    },
    [],
  )

  const setValues = useCallback((partial: Partial<T>): void => {
    setValuesState((prev) => ({ ...prev, ...partial }))
  }, [])

  const validate = (): T | null => {
    const result = schema.safeParse(values)
    if (result.success) {
      setErrors({})
      return result.data
    }
    const fieldErrors = result.error.issues.reduce<FormErrors<T>>(
      (acc, issue) => {
        const key = issue.path[0]
        if (isKeyOf(key, defaults) && !(key in acc)) {
          Object.defineProperty(acc, key, {
            value: issue.message,
            enumerable: true,
            configurable: true,
            writable: true,
          })
        }
        return acc
      },
      {},
    )
    setErrors(fieldErrors)
    return null
  }

  const reset = useCallback((): void => {
    setValuesState(defaults)
    setErrors({})
    hydratedRef.current = false
  }, [defaults])

  return { values, errors, setField, setValues, validate, reset }
}
